import React, { useCallback, useState } from 'react';

import { Alert, Box } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';

import {
  RATE_CODE_INITIAL_SEARCH,
  RateCodeSearchState,
  rateCodeColumns,
} from '~/components/Cruises/components/shared/RateCodeGridConfig';
import RateCodeSearchForm from '~/components/Cruises/components/shared/RateCodeSearchForm';

import { RATE_CODE_PAGE_SIZE, useGetRateCodes } from '~/hooks/apis/cruises/useGetRateCodes';

interface Props {
  vendorCode: string | undefined;
  selectedRateIds: Array<string>;
  existingRateIds?: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
}

export default function RateCodeFilterTab({
  vendorCode,
  selectedRateIds,
  existingRateIds = [],
  onSelectionChange,
}: Props) {
  const [searchFields, setSearchFields] = useState<RateCodeSearchState>(RATE_CODE_INITIAL_SEARCH);
  const [committedSearch, setCommittedSearch] = useState<RateCodeSearchState>(RATE_CODE_INITIAL_SEARCH);
  const [page, setPage] = useState(0);

  const { data, isFetching, isError } = useGetRateCodes(vendorCode, committedSearch, page);

  const rows = data?.result ?? [];

  const handleSearch = useCallback(() => {
    setPage(0);
    setCommittedSearch(searchFields);
  }, [searchFields]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const ids = Array.isArray(model) ? model.map(String) : [];
      onSelectionChange(ids.filter((id) => !existingRateIds.includes(id)));
    },
    [onSelectionChange, existingRateIds],
  );

  if (!vendorCode) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No cruise line associated with this inclusion. Rate code filters cannot be added.
      </Alert>
    );
  }

  return (
    <Box>
      <RateCodeSearchForm
        searchFields={searchFields}
        onSearchFieldsChange={setSearchFields}
        onSearch={handleSearch}
        isSearching={isFetching}
        onKeyDown={handleKeyDown}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load rate codes. Please try again.
        </Alert>
      )}

      <DataGrid
        loading={isFetching}
        rows={rows}
        columns={rateCodeColumns}
        getRowId={(row) => row.id}
        checkboxSelection
        rowSelectionModel={[...existingRateIds, ...selectedRateIds]}
        onRowSelectionModelChange={handleSelectionChange}
        isRowSelectable={(params) => !existingRateIds.includes(params.id as string)}
        keepNonExistentRowsSelected
        disableRowSelectionOnClick={false}
        pagination
        paginationMode="server"
        rowCount={data?.total ?? 0}
        pageSizeOptions={[RATE_CODE_PAGE_SIZE]}
        paginationModel={{ page, pageSize: RATE_CODE_PAGE_SIZE }}
        onPaginationModelChange={({ page: newPage }) => setPage(newPage)}
        autoHeight
        disableColumnMenu
      />
    </Box>
  );
}
